import TextLink from 'LuxKit/TextLink'
import Caption from 'LuxKit/Typography/Caption'
import config from 'constants/config'
import { staticPageMessages } from 'constants/config/i18nMessages'
import { isConsentRequiredOnClient } from '../../../consent/clientScope'
import { useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

interface Props {
  modalType?: string
  colour?: 'light-foreground-alt' | 'neutral-three'
  /** Account-creation checkbox. Uses the create-account sentence when marketing is split out. */
  accountCreation?: boolean
}

function AccountAccessLegalText({ modalType, colour = 'light-foreground-alt', accountCreation = false }: Props) {
  const intl = useIntl()
  const isPhonePromptModal = modalType === 'promptAddPhone'
  // Metasearch guest checkout discloses the account we create on the customer's
  // behalf, but not loyalty enrolment or marketing consent: those are handled
  // outside this flow. Registration still emits USER_SIGN_UP with nothing marking
  // it as a guest, so keep this copy in step with what its consumers actually do.
  const isGuestCheckout = modalType === 'guestCheckout'
  const termsURL = config.contentLinks.termsAndConditions.href || config.contentLinks.termsAndConditions.to!
  const privacyURL = config.contentLinks.privacyPolicy.href || config.contentLinks.privacyPolicy.to!
  const loyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const privacyPolicyText = intl.formatMessage(staticPageMessages.privacyPolicy)
  const separateMarketing = !isGuestCheckout && !isPhonePromptModal && isConsentRequiredOnClient()
  const loyaltyProgram = getLoyaltyProgramDisplayName()
  const termsLink = (chunks: React.ReactNode) => <TextLink href={termsURL} rel="noopener noreferrer" target="_blank" weight="regular" colour={colour}>{chunks}</TextLink>
  const privacyLink = (chunks: React.ReactNode) => <TextLink href={privacyURL} rel="noopener noreferrer" target="_blank" weight="regular" colour={colour}>{chunks}</TextLink>

  return (
    <Caption colour={colour} variant="medium">
      {isPhonePromptModal && (
        <FormattedMessage
          id="f7gNggeLDLd_akEFMuK5"
          defaultMessage="By adding your mobile number, you agree to our <termsLink>Terms & Conditions</termsLink> and <privacyLink>{privacyPolicy},</privacyLink> and to subscribe to marketing communications for offers, alerts and services."
          description="Legal text for phone number addition with T&Cs and privacy policy links"
          values={{
            privacyPolicy: privacyPolicyText,
            termsLink: (chunks: React.ReactNode) => <TextLink href={termsURL} rel="noopener noreferrer" target="_blank" weight="regular" colour={colour}>{chunks}</TextLink>,
            privacyLink: (chunks: React.ReactNode) => <TextLink href={privacyURL} rel="noopener noreferrer" target="_blank" weight="regular" colour={colour}>{chunks}</TextLink>,
          }}
        />
      )}
      {isGuestCheckout && (
        <FormattedMessage
          id="IE4OoLI-O_hIRu9mryRB"
          defaultMessage="You’ll enter your details at checkout so we can process and manage your booking. By continuing, you agree to our <termsLink>Terms & Conditions</termsLink> and <privacyLink>{privacyPolicy}</privacyLink>."
          description="Legal text under the guest checkout button explaining booking details and agreement terms"
          values={{
            privacyPolicy: privacyPolicyText,
            termsLink: (chunks: React.ReactNode) => <TextLink href={termsURL} rel="noopener noreferrer" target="_blank" weight="regular" colour={colour}>{chunks}</TextLink>,
            privacyLink: (chunks: React.ReactNode) => <TextLink href={privacyURL} rel="noopener noreferrer" target="_blank" weight="regular" colour={colour}>{chunks}</TextLink>,
          }}
        />
      )}
      {!isGuestCheckout && !isPhonePromptModal && loyaltyEnabled && separateMarketing && accountCreation && (
        <FormattedMessage
          id="oO4pJcs-yDrvkABqTx6P"
          defaultMessage="By creating an account, you agree to our <termsLink>Terms & Conditions</termsLink>, join the {loyaltyProgram} program, and confirm you have read our <privacyLink>{privacyPolicy}</privacyLink>."
          description="Required account-creation legal text when marketing consent is collected separately"
          values={{ privacyPolicy: privacyPolicyText, loyaltyProgram, termsLink, privacyLink }}
        />
      )}
      {!isGuestCheckout && !isPhonePromptModal && loyaltyEnabled && separateMarketing && !accountCreation && (
        <FormattedMessage
          id="rN1GanGlaGiV8FLdu9bD"
          defaultMessage="By continuing, you agree to our <termsLink>Terms & Conditions</termsLink>, join the {loyaltyProgram} program, and confirm you have read our <privacyLink>{privacyPolicy}</privacyLink>."
          description="Required continue legal text when marketing consent is collected separately"
          values={{ privacyPolicy: privacyPolicyText, loyaltyProgram, termsLink, privacyLink }}
        />
      )}
      {!isGuestCheckout && !isPhonePromptModal && loyaltyEnabled && !separateMarketing && (
        <FormattedMessage
          id="sK1qEmWqIu7rciOqhrmT"
          defaultMessage="By continuing, you agree to our <termsLink>Terms & Conditions</termsLink> and <privacyLink>{privacyPolicy},</privacyLink> join the {loyaltyProgram} program, and to subscribe to marketing communications for offers, alerts and services."
          description="Account sign-up legal text linking to terms and privacy policy, with loyalty program enrolment and marketing consent"
          values={{ privacyPolicy: privacyPolicyText, loyaltyProgram, termsLink, privacyLink }}
        />
      )}
      {!isGuestCheckout && !isPhonePromptModal && !loyaltyEnabled && separateMarketing && accountCreation && (
        <FormattedMessage
          id="YQpdPOtbFpMh4z6MBE_y"
          defaultMessage="By creating an account, you agree to our <termsLink>Terms & Conditions</termsLink> and confirm you have read our <privacyLink>{privacyPolicy}</privacyLink>."
          description="Required account-creation legal text without a loyalty program when marketing consent is collected separately"
          values={{ privacyPolicy: privacyPolicyText, termsLink, privacyLink }}
        />
      )}
      {!isGuestCheckout && !isPhonePromptModal && !loyaltyEnabled && separateMarketing && !accountCreation && (
        <FormattedMessage
          id="NN8YsTImP1Ql4saCnz1q"
          defaultMessage="By continuing, you agree to our <termsLink>Terms & Conditions</termsLink> and confirm you have read our <privacyLink>{privacyPolicy}</privacyLink>."
          description="Required continue legal text without a loyalty program when marketing consent is collected separately"
          values={{ privacyPolicy: privacyPolicyText, termsLink, privacyLink }}
        />
      )}
      {!isGuestCheckout && !isPhonePromptModal && !loyaltyEnabled && !separateMarketing && (
        <FormattedMessage
          id="znipBfcVHP8gqcqagn18"
          defaultMessage="By continuing, you agree to our <termsLink>Terms & Conditions</termsLink> and <privacyLink>{privacyPolicy},</privacyLink> and to subscribe to marketing communications for offers, alerts and services."
          description="Legal text for sign-up with T&Cs and privacy policy links"
          values={{ privacyPolicy: privacyPolicyText, termsLink, privacyLink }}
        />
      )}
    </Caption>
  )
}

export default AccountAccessLegalText
